"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SqlGenerator = void 0;
const ast_1 = require("../ast");
const utils_1 = require("../utils");
// Generic AST-to-SQL generator. All dialect-specific behaviour lives in
// the DialectConfig passed to the constructor — there are no subclasses.
// Fields the config doesn't set fall back to ANSI-standard defaults.
class SqlGenerator {
    constructor(options, dialect) {
        this.options = options;
        this.dialect = dialect;
    }
    // Public entry point. Dispatches to the node-specific generator, then
    // hands aggregate output to the caller-supplied `renderAggregate` callback
    // (if any) so the caller decides how to embed the aggregate in their SQL
    // context — e.g. window-wrap for post-aggregation SELECTs, pass through
    // for GROUP BY contexts. Every recursive call from child arms goes back
    // through `generate()`, so the hook is applied once per aggregate node
    // anywhere in the tree.
    generate(node) {
        const sql = this.generateNode(node);
        if ((0, ast_1.isAggregateCall)(node) && this.options.renderAggregate) {
            return this.options.renderAggregate(sql);
        }
        return sql;
    }
    generateNode(node) {
        switch (node.type) {
            case 'BinaryOp':
                return this.generateBinaryOp(node);
            case 'UnaryOp':
                return this.generateUnaryOp(node);
            case 'If':
                return this.generateIf(node);
            case 'ConditionalAggregate':
                return this.generateConditionalAggregate(node);
            case 'CountIf':
                return this.generateCountIf(node);
            case 'ZeroArgFn':
                return this.generateZeroArgFn(node);
            case 'SingleArgFn':
                return this.generateSingleArgFn(node);
            case 'OneOrTwoArgFn':
                return this.generateOneOrTwoArgFn(node);
            case 'ZeroOrOneArgFn':
                return this.generateZeroOrOneArgFn(node);
            case 'VariadicFn':
                return this.generateVariadicFn(node);
            case 'WindowFn':
                return this.generateWindowFn(node);
            case 'ColumnRef':
                return this.generateColumnRef(node);
            case 'NumberLiteral':
                return this.generateNumberLiteral(node);
            case 'StringLiteral':
                return this.generateStringLiteral(node);
            case 'BooleanLiteral':
                return this.generateBooleanLiteral(node);
            case 'Comparison':
                return this.generateComparison(node);
            case 'Logical':
                return this.generateLogical(node);
            case 'WindowClause':
                throw new Error('WindowClause should not be generated directly');
            default:
                return (0, utils_1.assertUnreachable)(node, `Unknown node type: ${node.type}`);
        }
    }
    generateBinaryOp(node) {
        const left = this.generate(node.left);
        const right = this.generate(node.right);
        switch (node.op) {
            case '+':
            case '-':
            case '*':
                return `(${left} ${node.op} ${right})`;
            case '/':
                return `(${left} / NULLIF(${right}, 0))`;
            case '^':
                return `POWER(${left}, ${right})`;
            case '%':
                return this.generateModulo(left, right);
            default:
                return (0, utils_1.assertUnreachable)(node.op, `Unknown operator: ${node.op}`);
        }
    }
    generateUnaryOp(node) {
        const operand = this.generate(node.operand);
        switch (node.op) {
            case '-':
                return `(-(${operand}))`;
            case 'NOT':
                return `(NOT ${operand})`;
            default:
                return (0, utils_1.assertUnreachable)(node.op, `Unknown unary op: ${node.op}`);
        }
    }
    generateIf(node) {
        const condition = this.generate(node.condition);
        const then = this.generate(node.then);
        const else_ = node.else ? this.generate(node.else) : 'NULL';
        return `CASE WHEN ${condition} THEN ${then} ELSE ${else_} END`;
    }
    generateConditionalAggregate(node) {
        const value = this.generate(node.value);
        const condition = this.generate(node.condition);
        switch (node.name) {
            case 'SUMIF':
                return `SUM(CASE WHEN ${condition} THEN ${value} END)`;
            case 'AVERAGEIF':
                return `AVG(CASE WHEN ${condition} THEN ${value} END)`;
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown conditional aggregate: ${node.name}`);
        }
    }
    generateCountIf(node) {
        const condition = this.generate(node.condition);
        return `COUNT(CASE WHEN ${condition} THEN 1 END)`;
    }
    generateZeroArgFn(node) {
        switch (node.name) {
            case 'TODAY':
                return this.generateToday();
            case 'NOW':
                return this.generateNow();
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown zero-arg function: ${node.name}`);
        }
    }
    generateSingleArgFn(node) {
        const arg = this.generate(node.arg);
        switch (node.name) {
            case 'ABS':
                return `ABS(${arg})`;
            case 'CEIL':
            case 'CEILING':
                return `CEIL(${arg})`;
            case 'FLOOR':
                return `FLOOR(${arg})`;
            case 'LEN':
            case 'LENGTH':
                return this.generateLength(arg);
            case 'TRIM':
                return `TRIM(${arg})`;
            case 'LOWER':
                return `LOWER(${arg})`;
            case 'UPPER':
                return `UPPER(${arg})`;
            case 'YEAR':
                return this.generateExtract('YEAR', arg);
            case 'MONTH':
                return this.generateExtract('MONTH', arg);
            case 'DAY':
                return this.generateExtract('DAY', arg);
            case 'ISNULL':
                return `(${arg} IS NULL)`;
            case 'SUM':
                return `SUM(${arg})`;
            case 'AVERAGE':
            case 'AVG':
                return this.generateAvg(arg);
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown single-arg function: ${node.name}`);
        }
    }
    generateOneOrTwoArgFn(node) {
        const args = node.args.map((a) => this.generate(a));
        switch (node.name) {
            case 'ROUND':
                if (this.dialect.generateRound) {
                    return this.dialect.generateRound(args[0], args[1]);
                }
                return `ROUND(${args[0]}${args[1] !== undefined ? `, ${args[1]}` : ''})`;
            case 'MIN':
                return args.length === 1
                    ? `MIN(${args[0]})`
                    : `LEAST(${args.join(', ')})`;
            case 'MAX':
                return args.length === 1
                    ? `MAX(${args[0]})`
                    : `GREATEST(${args.join(', ')})`;
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown one-or-two-arg function: ${node.name}`);
        }
    }
    generateZeroOrOneArgFn(node) {
        switch (node.name) {
            case 'COUNT':
                return node.arg
                    ? `COUNT(${this.generate(node.arg)})`
                    : 'COUNT(*)';
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown zero-or-one-arg function: ${node.name}`);
        }
    }
    generateVariadicFn(node) {
        const args = node.args.map((a) => this.generate(a));
        switch (node.name) {
            case 'CONCAT':
                return this.generateConcat(args);
            case 'COALESCE':
                return `COALESCE(${args.join(', ')})`;
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown variadic function: ${node.name}`);
        }
    }
    generateWindowFn(node) {
        const args = node.args.map((a) => this.generate(a));
        switch (node.name) {
            case 'ROW_NUMBER':
                return this.generateWindowFunction('ROW_NUMBER', [], node);
            case 'RANK':
                return this.generateWindowFunction('RANK', [], node);
            case 'DENSE_RANK':
                return this.generateWindowFunction('DENSE_RANK', [], node);
            case 'RUNNING_TOTAL':
                return this.generateWindowFunction('SUM', [args[0]], node, 'ROWS UNBOUNDED PRECEDING');
            case 'NTILE':
                return this.generateWindowFunction('NTILE', [args[0]], node);
            // FIRST_VALUE and LAST_VALUE both get an explicit
            // `UNBOUNDED PRECEDING..UNBOUNDED FOLLOWING` frame. For
            // LAST_VALUE it's required — the ANSI default frame ends at
            // CURRENT ROW, so without this LAST_VALUE returns the current
            // row (not the last). For FIRST_VALUE it's a semantic no-op
            // (the first row of the partition is the first row regardless
            // of the frame's upper bound) but required on Redshift, which
            // rejects aggregate-style windows with ORDER BY and no frame.
            case 'FIRST':
            case 'LAST': {
                const sqlFn = node.name === 'FIRST' ? 'FIRST_VALUE' : 'LAST_VALUE';
                return this.generateWindowFunction(sqlFn, [args[0]], node, 'ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING');
            }
            case 'LAG':
            case 'LEAD':
                return this.dispatchLagLead(node.name, args, node);
            // TODO: unsafe cast — MOVING_SUM/MOVING_AVG assume second arg is a NumberLiteral
            // but the grammar accepts any Expression. Fix by adding a grammar rule that
            // enforces NumberLiteral in the second position (same pattern as BooleanExpression).
            case 'MOVING_SUM': {
                const preceding = node.args[1].value;
                return this.generateWindowFunction('SUM', [args[0]], node, `ROWS BETWEEN ${preceding} PRECEDING AND CURRENT ROW`);
            }
            case 'MOVING_AVG': {
                const preceding = node.args[1].value;
                // Route through generateAvg so dialects that need to
                // preserve precision across the AVG division (Redshift)
                // can inject a cast on the value argument.
                return this.appendOverClause(this.generateAvg(args[0]), node, `ROWS BETWEEN ${preceding} PRECEDING AND CURRENT ROW`);
            }
            default:
                return (0, utils_1.assertUnreachable)(node.name, `Unknown window function: ${node.name}`);
        }
    }
    generateColumnRef(node) {
        const column = this.options.columns[node.name];
        if (!column) {
            throw new Error(`Unknown column reference: ${node.name}. Available columns: ${Object.keys(this.options.columns).join(', ')}`);
        }
        return this.quoteIdentifier(column);
    }
    generateNumberLiteral(node) {
        return String(node.value);
    }
    generateBooleanLiteral(node) {
        return node.value ? 'TRUE' : 'FALSE';
    }
    generateComparison(node) {
        const left = this.generate(node.left);
        const right = this.generate(node.right);
        return `(${left} ${node.op} ${right})`;
    }
    generateLogical(node) {
        const left = this.generate(node.left);
        const right = this.generate(node.right);
        return `(${left} ${node.op} ${right})`;
    }
    // --- Dialect-configurable emission ---
    // These methods consult the DialectConfig and fall back to ANSI-standard
    // defaults when the dialect doesn't override.
    quoteIdentifier(name) {
        return this.dialect.quoteIdentifier(name);
    }
    generateStringLiteral(node) {
        if (this.dialect.generateStringLiteral) {
            return this.dialect.generateStringLiteral(node);
        }
        const escaped = node.value.replace(/'/g, "''");
        return `'${escaped}'`;
    }
    generateModulo(left, right) {
        return (this.dialect.generateModulo?.(left, right) ??
            `MOD(${left}, ${right})`);
    }
    // LAG / LEAD dispatch: hand off to the dialect's `generateLagLead` if
    // present, otherwise use the ANSI-compatible default (bare LAG/LEAD
    // with default frame, variadic args). The dialect callback receives
    // an `emitWindow` bound to this node's window clause so dialects don't
    // have to own PARTITION BY / ORDER BY plumbing.
    dispatchLagLead(sqlFunc, args, node) {
        const emitWindow = (fn, funcArgs, frameClause) => this.generateWindowFunction(fn, funcArgs, node, frameClause);
        if (this.dialect.generateLagLead) {
            return this.dialect.generateLagLead({
                sqlFunc,
                args,
                emitWindow,
            });
        }
        return emitWindow(sqlFunc, args);
    }
    // --- ANSI defaults shared across all dialects today ---
    // Promote to DialectConfig fields when a real dialect first diverges.
    generateConcat(args) {
        if (this.dialect.generateConcat) {
            return this.dialect.generateConcat(args);
        }
        return `CONCAT(${args.join(', ')})`;
    }
    generateAvg(arg) {
        if (this.dialect.generateAvg) {
            return this.dialect.generateAvg(arg);
        }
        return `AVG(${arg})`;
    }
    generateLength(expr) {
        return `LENGTH(${expr})`;
    }
    generateToday() {
        return 'CURRENT_DATE';
    }
    generateNow() {
        return 'CURRENT_TIMESTAMP';
    }
    generateExtract(part, expr) {
        return `EXTRACT(${part} FROM ${expr})`;
    }
    // Attach an OVER (…) clause to a pre-built function-call string. Lets
    // callers that need per-function argument transforms (e.g. AVG's
    // precision-preserving cast on Redshift) build the call via their own
    // emitter and still share the PARTITION BY / ORDER BY / frame plumbing.
    //
    // When the formula has no explicit ORDER BY in its OVER clause we fall
    // back to `options.defaultOrderBy` (if set). That option carries the
    // containing query's visual sort order, so `LAG(x)` without an explicit
    // ORDER BY picks the row the user sees immediately above the current
    // one in the rendered table. It also makes BigQuery and Snowflake
    // accept `LAG`/`LEAD`/`ROW_NUMBER`/... which reject an OVER clause with
    // no ORDER BY. An explicit ORDER BY in the formula always wins.
    appendOverClause(funcCall, node, frameClause) {
        const overParts = [];
        const wc = node.windowClause;
        if (wc?.partitionBy) {
            overParts.push(`PARTITION BY ${this.generate(wc.partitionBy)}`);
        }
        if (wc?.orderBy) {
            const dir = wc.orderBy.direction ? ` ${wc.orderBy.direction}` : '';
            overParts.push(`ORDER BY ${this.generate(wc.orderBy.column)}${dir}`);
        }
        else {
            const defaultOrder = this.options.defaultOrderBy;
            if (defaultOrder && defaultOrder.length > 0) {
                const cols = defaultOrder
                    .map((entry) => {
                    const dir = entry.direction
                        ? ` ${entry.direction}`
                        : '';
                    return `${this.quoteIdentifier(entry.column)}${dir}`;
                })
                    .join(', ');
                overParts.push(`ORDER BY ${cols}`);
            }
        }
        const framePart = frameClause ? ` ${frameClause}` : '';
        return `${funcCall} OVER (${overParts.join(' ')}${framePart})`;
    }
    generateWindowFunction(sqlFunc, funcArgs, node, frameClause) {
        const funcCall = funcArgs.length > 0
            ? `${sqlFunc}(${funcArgs.join(', ')})`
            : `${sqlFunc}()`;
        return this.appendOverClause(funcCall, node, frameClause);
    }
}
exports.SqlGenerator = SqlGenerator;
//# sourceMappingURL=generator.js.map